/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2017, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 */
define([
	'./postProcess/LocalAggregateSortInfo',
	'../../dashboard/queryService/QuerySelectionBuilder',
	'underscore'
], function(
	LocalAggregateSortInfo,
	QuerySelectionBuilder,
	_
) {
	class VisQuerySelectionBuilder extends QuerySelectionBuilder{
		constructor({ internalVisualization, layerToQuerySlotsMap, applyServerSort }) {
			super();
			this.internalVisualization = internalVisualization;
			this._querySlotSortPriority = 0;
			this._serverSort = applyServerSort;
			this._localAggregateSortInfo = new LocalAggregateSortInfo(internalVisualization);
			this._querySlotSortPriority = this._getDataItemsHighestSortPriority(layerToQuerySlotsMap) + 1;
		}

		_forEachQuerySlotDataItem(layerToQuerySlotsMap, callback) {
			layerToQuerySlotsMap.forEach((querySlots)=>{
				querySlots.forEach(slot=>{
					const dataItems = slot.getDataItemList();
					dataItems.forEach(item=>{
						callback(item, slot);
					});
				});
			});
		}

		/**
     * override the super build.
     */
		build(dataItem, slot, outputEdgeFilterExceptions) {
			const finalSelections = [];
			if (!dataItem.getBinning()) {
				//By default, return selections in the order agreed to by PM.
				finalSelections.push(...this._buildSelectionsFromDrillStateForHierarchyOrSet(dataItem));
				finalSelections.push(...this._buildSelectionsFromLocalFiltersForOLAPDataItems(dataItem, outputEdgeFilterExceptions));
				finalSelections.push(...this._buildSelectionsFromSearchFiltersForOLAPDataItems(dataItem, outputEdgeFilterExceptions));
				finalSelections.push(...this._buildSelectionsForTopBottom(dataItem));
				finalSelections.push(...this._buildSelectionsForSort(dataItem, slot));
			} else {
				finalSelections.push(...this._buildSelectionsFromLocalFiltersForOLAPDataItems(dataItem, outputEdgeFilterExceptions));
				finalSelections.push(...this._buildSelectionsFromSearchFiltersForOLAPDataItems(dataItem, outputEdgeFilterExceptions));
				finalSelections.push(...this._buildSelectionsForSort(dataItem, slot));
			}

			return finalSelections;
		}

		//For hierarchies or OLAP set, build one of root members, children of single root or children of current drill member.
		_buildSelectionsFromDrillStateForHierarchyOrSet(dataItem) {
			const localFilters = this.internalVisualization.getLocalFilters();
			const filterList = localFilters && localFilters.getFilterList() || [];
			return super._buildSelectionsFromDrillStateForHierarchyOrSet(dataItem, filterList);
		}

		_buildSelectionsFromSearchFiltersForOLAPDataItems(dataItem, outputEdgeFilterExceptions) {
			const searchFilterList = this.internalVisualization.getSearchFilterList();
			return super._buildSelectionsFromFiltersForOLAPDataItems(dataItem, searchFilterList, outputEdgeFilterExceptions);
		}

		_buildSelectionsFromLocalFiltersForOLAPDataItems(dataItem, outputEdgeFilterExceptions) {
			const localFilters = this.internalVisualization.getLocalFilters();
			const localFilterList = localFilters.getFilterList();
			return super._buildSelectionsFromFiltersForOLAPDataItems(dataItem, localFilterList, outputEdgeFilterExceptions);
		}

		/*
		 * Sort application priority:
		 * Apply user defined sort if there is one,
	 	 * Apply model defined sort if there is one and user defined sort doesn't exit,
		 * Apply default sort
		 */
		_buildSelectionsForSort(dataItem, slot) {
			const bClientSortEnabled = this._serverSort ? false : this._isClientSideSortEnabledForDataItem(dataItem);

			let selections = [];
			if (!bClientSortEnabled) {
				const dataItemOrModelSort = dataItem.getSort();
				let sort = !dataItem.hasDefaultSort() && dataItemOrModelSort;
				sort = sort || dataItem.hasModelSort() && dataItemOrModelSort;

				let sortObj = sort && sort.type ? sort : null;
				const isIgnoreSlotSort = this._ignoreDefaultSlotSort(dataItem);

				// 3. Default sort: preserve the hierarchical order for Olap column.
				if (!sortObj && !isIgnoreSlotSort) {
					sortObj = this._getDefaultSort(slot, dataItem);
					if(sortObj){
						sortObj.priority = this._querySlotSortPriority++;
					}
				}

				if (sortObj) {
					const sortSelectionSpec = {
						'operation': 'order',
						'sort': _.pick(sortObj, 'type', 'by', 'priority')
					};

					if (sortObj.context){
						sortSelectionSpec.context = [{
							itemId: sortObj.context
						}];
					}
					selections = [sortSelectionSpec];
				}
			}
			return selections;
		}

		_isClientSideSortEnabledForDataItem(dataItem) {
			const localSortInfo = this._localAggregateSortInfo.getSortInfo();
			if (localSortInfo) {
				return localSortInfo.aggregateDataItemId === dataItem.getId();
			}
			return false;
		}

		/**
		 *
		 * @param {*} slot
		 * @param {*} dataItem
		 * todo: the original implementation at VisDataItemManager.getDefaultSort() need to be removed once useAPI.
		 */
		_getDefaultSort (slot, dataItem) {
			let sortObj = null;
			let sortType;
			const topBottomInfo = dataItem.getTopBottom();
			if (topBottomInfo) {
				const topBottomType = topBottomInfo.type;
				sortType = (topBottomType === 'topcount' || topBottomType === 'toppercent') ? 'desc' : 'asc';
				sortObj = {
					type: sortType
				};
				if (topBottomInfo.context) {
					//Dataitem with top/bottom will always sort by 'value'.
					sortObj.by = 'value';
					sortObj.context = topBottomInfo.context.itemId;
				}
			} else {
				// 2. Sort from slot definition, but only if there is no topBottom set
				sortType = slot.getDefinition().getSort();
				// since after binning, dataItem type is attribute, if there is no sort on slot and measure add 'asc' sort
				if (!sortType && !!dataItem.getBinning()) {
					sortType = 'asc';
				}
				if (sortType){
					sortObj = {
						type: sortType
					};
				}
			}
			return sortObj;
		}

		/**
		 *
		 * Default sort is ignored in the following cases:
		 *  - data source is OLAP or NamedSet and no topBottom is defined or no rank is defined for the dataItem
		 *  - data source is not OLAP and is not NamedSet and no topBottom is defined up to the passed one and one topBottom is defined after it
		 * @param {Object} dataItem - current dataItem; see {@link DataItemAPI}
		 * @returns {boolean} true if the default sort is to be ignored, false otherwise
		 *
		 * todo: the original implementation at VisDataSlotsManager.ignoreDefaultSlotSort() need to be removed once useAPI.
		 */
		_ignoreDefaultSlotSort(dataItem) {
			let ignore = false;
			const dataItemMappingInfoList = this.internalVisualization.getSlots().getMappingInfoList();
			let indexInMappingInfoList = -1;
			_.find(dataItemMappingInfoList, (dataItemMappingObj, ind)=>{
				if (dataItemMappingObj.dataItem.getId() === dataItem.getId()) {
					indexInMappingInfoList = ind;
					return true;
				}
				return false;
			});
			const dataitemTopBottom = dataItem.getTopBottom();
			if (dataItem.getMetadataColumn().isOlapColumn() || dataItem.getMetadataColumn().isNamedSet()) {
				ignore = !dataitemTopBottom || !dataitemTopBottom.rank;
			} else {
				ignore = !this._hasPreviousTopBottom(dataItemMappingInfoList, indexInMappingInfoList)
				&& !dataitemTopBottom
				&& this._hasFollowedTopBottom(dataItemMappingInfoList, indexInMappingInfoList);
			}

			return ignore;
		}

		/**
		 * check if there is any topBottom definition previous to the passed index
		 * @param {Object} dataItemMappingInfoList - arrays of mappingInfo
		 * @param {number} index - index of the current dataItem
		 * @returns true if there is a topBottom definition defined before the passed index, false otherwise
		 */
		_hasPreviousTopBottom(dataItemMappingInfoList, index) {
			let hasPrevious = false;
			for (let i=0; i<index; i++) {
				if (dataItemMappingInfoList[i].dataItem.getTopBottom()) {
					hasPrevious = true;
					break;
				}
			}
			return hasPrevious;
		}

		/**
		 * check if there is any topBottom definition after to the passed index
		 * @param {Object} dataItemMappingInfoList - arrays of mappingInfo
		 * @param {number} index - index of the current dataItem
		 * @returns true if there is a topBottom definition defined after the passed index, false otherwise
		 */
		_hasFollowedTopBottom(dataItemMappingInfoList, index) {
			let hasFollowed = false;
			for (let i=index+1; i<dataItemMappingInfoList.length; i++) {
				if (dataItemMappingInfoList[i].dataItem.getTopBottom()) {
					hasFollowed = true;
					break;
				}
			}
			return hasFollowed;
		}

		_getDataItemsHighestSortPriority(layerToQuerySlotsMap) {
			let maxPriority = 0;
			this._forEachQuerySlotDataItem(layerToQuerySlotsMap, (dataItem)=>{
				const sortObj = dataItem.getSort();
				if (sortObj && maxPriority < sortObj.priority) {
					maxPriority = sortObj.priority;
				}
			});
			return maxPriority;
		}

	}
	return VisQuerySelectionBuilder;
});
